import type { Locale } from "@/types";

export type UseCaseSectorId =
  | "professional-services"
  | "health-wellness"
  | "construction-renovation"
  | "real-estate"
  | "beauty-care"
  | "automotive"
  | "retail"
  | "food-hospitality"
  | "training-coaching"
  | "technology-digital"
  | "finance-insurance"
  | "arts-events";

export interface UseCaseSectorMeta {
  id: UseCaseSectorId;
  /**
   * camelCase locale key used in src/locales/{fr,en}.json under
   * `useCaseSectors.{localeKey}`. Required because sector ids are kebab-case
   * and JSON keys are camelCase.
   */
  localeKey: string;
  slugs: Record<Locale, string>;
  /** True when the sector has full locale content and a live page. */
  published?: boolean;
}

export const USE_CASE_SECTORS: UseCaseSectorMeta[] = [
  {
    id: "professional-services",
    localeKey: "professionalServices",
    slugs: { fr: "services-professionnels", en: "professional-services" },
    published: true,
  },
  {
    id: "health-wellness",
    localeKey: "healthWellness",
    slugs: { fr: "sante-bien-etre", en: "health-wellness" },
    published: true,
  },
  {
    id: "construction-renovation",
    localeKey: "constructionRenovation",
    slugs: { fr: "construction-renovation", en: "construction-renovation" },
    published: true,
  },
  {
    id: "real-estate",
    localeKey: "realEstate",
    slugs: { fr: "immobilier", en: "real-estate" },
    published: true,
  },
  {
    id: "beauty-care",
    localeKey: "beautyCare",
    slugs: { fr: "beaute-soins", en: "beauty-care" },
    published: true,
  },
  {
    id: "automotive",
    localeKey: "automotive",
    slugs: { fr: "automobile", en: "automotive" },
    published: true,
  },
  {
    id: "retail",
    localeKey: "retail",
    slugs: { fr: "commerce-detail", en: "retail" },
    published: true,
  },
  {
    id: "food-hospitality",
    localeKey: "foodHospitality",
    slugs: { fr: "restauration-hotellerie", en: "food-hospitality" },
    published: true,
  },
  {
    id: "training-coaching",
    localeKey: "trainingCoaching",
    slugs: { fr: "formation-coaching", en: "training-coaching" },
    published: true,
  },
  {
    id: "technology-digital",
    localeKey: "technologyDigital",
    slugs: { fr: "technologies-numerique", en: "technology-digital" },
    published: true,
  },
  {
    id: "finance-insurance",
    localeKey: "financeInsurance",
    slugs: { fr: "finance-assurance", en: "finance-insurance" },
    published: true,
  },
  {
    id: "arts-events",
    localeKey: "artsEvents",
    slugs: { fr: "arts-evenements", en: "arts-events" },
    published: true,
  },
];

export const USE_CASE_SECTOR_IDS: UseCaseSectorId[] = USE_CASE_SECTORS.map(
  (s) => s.id,
);

export function getUseCaseSector(id: string): UseCaseSectorMeta | undefined {
  return USE_CASE_SECTORS.find((s) => s.id === id);
}

export function getUseCaseSectorByLocaleSlug(
  slug: string,
  locale: Locale,
): UseCaseSectorMeta | undefined {
  return USE_CASE_SECTORS.find((s) => s.slugs[locale] === slug);
}
